import { LogManager } from 'core/logging/LogManager';
import { httpClient } from 'core/net/http';
import * as v from 'valibot';
import { BlastModelCategory } from 'app/business-logic/domain-models/Blasting';
import { BlastModelTypeSchema } from 'app/business-logic/domain-models/Blasting.schema';

const logger = LogManager.getLogger('fetchBlastScenario', { error: { logToServer: true } });

export async function fetchBlastScenario(alias?: string) {
  const response = await httpClient.useInternalApi.asAuthorised().get(`/blasting/scenarios/${alias}`);
  const parsedData = v.safeParse(BlastScenarioResponseSchema, response.data);

  if (!response.isSuccess) {
    const errorMessage = `Failed to fetch blast scenario for alias "${alias}"`;
    logger.error(errorMessage);
    throw new Error(errorMessage);
  }

  if (!parsedData.success) {
    const errorMessage = `Failed to parse blast scenario response for alias "${alias}"`;
    logger.error(errorMessage, parsedData.issues);
    throw new Error(errorMessage);
  }
  return parsedData.output;
}

const LegendItemSchema = v.object({
  color: v.string(),
  description: v.string(),
  frontFaceDistanceInMetres: v.nullish(v.number()),
  backFaceDistanceInMetres: v.nullish(v.number()),
});

const LegendSchema = v.object({
  description: v.nullable(v.string()),
  items: v.array(LegendItemSchema),
  legendType: v.string(),
  title: v.string(),
});

const BlastModelSchema = v.object({
  category: v.enum(BlastModelCategory),
  id: v.string(),
  legends: v.array(LegendSchema),
  modelType: BlastModelTypeSchema,
});

const BlastJobStatusSchema = v.object({
  blastScenarioModelType: v.string(),
  blastingScenarioModelId: v.string(),
  error: v.nullable(v.string()),
  isComplete: v.boolean(),
  jobId: v.string(),
  progress: v.nullable(v.number()),
  utcCreatedTime: v.string(),
  utcFromDateTime: v.string(),
  utcLastUpdatedTime: v.string(),
  utcToDateTime: v.string(),
});

export const BlastScenarioResponseSchema = v.object({
  utcActualBlastTime: v.nullable(v.string()),
  utcBlastTimes: v.array(v.string()),
  utcLastUpdatedDateTime: v.string(),
  id: v.string(),
  name: v.string(),
  alias: v.string(),
  azimuth: v.nullable(v.number()),
  blastSetup: v.string(),
  backFaceKFactor: v.nullable(v.number()),
  blastModels: v.array(BlastModelSchema),
  burden: v.nullable(v.number()),
  chargeMass: v.nullable(v.number()),
  chargeMassPerHole: v.nullable(v.number()),
  displayUnitDescription: v.nullable(v.string()),
  displayUnitId: v.nullable(v.string()),
  dispersalAngle: v.nullable(v.number()),
  elevation: v.number(),
  forwardTrajectoryScientificModelDescription: v.nullable(v.string()),
  forwardTrajectoryScientificModelId: v.nullable(v.string()),
  frontFaceKFactor: v.nullable(v.number()),
  flyrockSiteConstantKv: v.nullable(v.number()),
  groundVibrationSiteConstantKv: v.nullable(v.number()),
  groundVibrationSiteExponentKb: v.nullable(v.number()),
  height: v.nullable(v.number()),
  holeAngle: v.nullable(v.number()),
  holeDiameter: v.nullable(v.number()),
  initialConcentrationGramsPerCubicMetre: v.nullable(v.number()),
  latitude: v.number(),
  longitude: v.number(),
  length: v.nullable(v.number()),
  modelPollutantId: v.nullable(v.string()),
  pointsOfInterest: v.array(v.number()),
  pollutantDescription: v.nullable(v.string()),
  pollutantScientificModelDescription: v.nullable(v.string()),
  pollutantScientificModelId: v.nullable(v.string()),
  stemmingHeight: v.nullable(v.number()),
  windScientificModelDescription: v.nullable(v.string()),
  windScientificModelId: v.nullable(v.string()),
  width: v.nullable(v.number()),
  jobStatuses: v.array(BlastJobStatusSchema),
});

export type BlastModel = v.InferOutput<typeof BlastModelSchema>;
